import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { SectionBase } from '../SectionBase';
import styled from 'styled-components';
import QuestionsText from './QuestionsText';
import TextField from '@mui/material/TextField';
import {
  MAX_WIDTH_FOR_SELECTOR,
  SelectorOptions,
} from 'shared/design-system/components/Selector';
import { getHelpText } from './HelperComponents';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import PrimaryBeneficiary, {
  PrimaryBeneficiaryProps,
} from './PrimaryBeneficiary';
import { logicModalImpactSummary } from './utils';
import { serviceDefinitions } from './serviceDefinitions';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useProgram } from 'shared/providers/ProgramProvider';

const OTHER_TEXT = 'Other';

const shouldShowOther = (
  questionId: number,
  value: string | undefined,
  otherText: string | undefined,
  onChange: (
    questionId: number,
    value: string,
    forcedValue?: number | null,
  ) => unknown,
) => {
  // We don't want to show the other field for this question
  if (questionId === 40003) {
    if (otherText) {
      onChange(questionId, (undefined as unknown) as string);
    }
    return false;
  }
  return value === OTHER_TEXT || otherText;
};

interface SectionServiceProps extends PrimaryBeneficiaryProps {
  title: string;
  subTitle: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any[];
  extraClass?: string;
  beneficiaryTerm?: string;
  intermediaries?: string;
  helpText: string | null;
  answers: Answer[];
  isProgramLevelQuestion?: boolean;
}

export const SectionServiceRender: React.FC<PrimaryBeneficiaryProps & {
  isProgramLevelQuestion?: boolean;
}> = ({ data, questionId, value, onChange, isProgramLevelQuestion }) => {
  const {
    subServiceQuestionId,
    intermediariesQuestionId,
    setSubService,
    setIntermediaries,
  } = useIndicator();
  const [other, setOther] = React.useState<string | undefined>(undefined);
  const options = useMemo(() => data?.map(row => row.name), [data]);

  const handleChange = useCallback(
    (value: string) => {
      if (value !== OTHER_TEXT) setOther(undefined);
      const serviceId =
        value !== OTHER_TEXT
          ? data.filter(d => d.name === value)?.[0]?.dbId
          : null;
      onChange(questionId, value, serviceId);
      if (questionId === subServiceQuestionId) {
        setSubService(value);
      }
      if (questionId === intermediariesQuestionId) {
        setIntermediaries(value);
      }
    },
    [
      data,
      onChange,
      questionId,
      subServiceQuestionId,
      intermediariesQuestionId,
      setSubService,
      setIntermediaries,
    ],
  );

  const handleWrite = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = ev.target.value;
    setOther(newVal);
    onChange(questionId, newVal);
    if (questionId === subServiceQuestionId) {
      setSubService(newVal);
    }
    if (questionId === intermediariesQuestionId) {
      setIntermediaries(newVal);
    }
  };

  useEffect(() => {
    if (value && options.indexOf(value) === -1) {
      setOther(value);
    }
    return () => {
      setOther(undefined);
    };
  }, [setOther, options, value]);

  // One-shot auto-populate from the program-level answer when the report
  // has no answer yet. Field stays editable. Mirrors the pattern used in
  // SectionServiceCollection / ActivitiesRender.
  const { answers: programAnswers } = useProgram();
  const programAnswerText = programAnswers?.[questionId]?.text;
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!isProgramLevelQuestion) return;
    if (!programAnswerText) return;
    if (value) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    const serviceId =
      programAnswerText !== OTHER_TEXT
        ? data?.filter(d => d.name === programAnswerText)?.[0]?.dbId ?? null
        : null;
    onChange(questionId, programAnswerText, serviceId);
  }, [
    isProgramLevelQuestion,
    programAnswerText,
    value,
    questionId,
    onChange,
    data,
  ]);

  return (
    <>
      <SelectorOptions
        variant="radio"
        options={options}
        getLabel={val => val}
        getSelected={val => (other ? val === OTHER_TEXT : value === val)}
        onClick={handleChange}
        tooltip={val =>
          serviceDefinitions[val as keyof typeof serviceDefinitions]
            ?.description
        }
      />
      {shouldShowOther(questionId, value, other, onChange) && (
        <OtherTextField
          value={other}
          placeholder="Enter your own term here"
          onChange={handleWrite}
          autoFocus={true}
        />
      )}
    </>
  );
};

export const SectionService: React.FC<SectionServiceProps> = ({
  data,
  title,
  subTitle,
  helpText,
  questionId,
  guidanceText,
  value,
  intermediaries,
  onChange,
  error,
  extraClass = '',
  answers,
  isProgramLevelQuestion,
}) => {
  const cause =
    questionId === 40004
      ? answers.find(x => x.questionId === 40003)?.text
      : undefined;
  const mainBeneficiaryText = cause
    ? logicModalImpactSummary[cause as keyof typeof logicModalImpactSummary]
    : undefined;
  const helperText = useMemo(
    () =>
      getHelpText(
        questionId,
        answers,
        helpText,
        mainBeneficiaryText,
        intermediaries,
      ),
    [helpText, questionId, answers],
  );

  const Component =
    questionId === 40004 ? PrimaryBeneficiary : SectionServiceRender;

  return (
    <SectionBase
      title={title}
      questionId={questionId}
      guidanceText={guidanceText}
      subTitle={
        <QuestionsText questionId={questionId} defaultTitle={subTitle} />
      }
      helpText={helperText}
      helpTextMaxWidth={questionId === 40004 ? 704 : undefined}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error={error as any}
      extraClass={extraClass}
    >
      <Component
        data={data}
        value={value}
        guidanceText={guidanceText}
        questionId={questionId}
        onChange={onChange}
        answers={answers}
        mainBeneficiaryText={mainBeneficiaryText}
        isProgramLevelQuestion={isProgramLevelQuestion}
      />
    </SectionBase>
  );
};

export const OtherTextField = styled(TextField)`
  width: 100%;
  max-width: ${MAX_WIDTH_FOR_SELECTOR};
  margin-bottom: ${SPACERS.extraLarge} !important;
  input {
    padding-top: ${SPACERS.small};
    padding-bottom: ${SPACERS.small};
  }
`;
